<template>
  <v-dialog
    :model-value="modelValue"
    max-width="820px"
    persistent
    scrollable
    data-testid="tax-config-v2-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card @keydown.esc="close">
      <v-card-title class="bg-primary text-white d-flex align-center pa-4">
        <v-icon class="mr-2">{{ titleIcon }}</v-icon>
        <div class="flex-grow-1">
          <div class="text-h6">{{ titleText }}</div>
        </div>
        <v-btn icon variant="text" color="white" data-testid="config-v2-close-btn" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <v-form ref="form">
          <!-- ── VERSION mode ─────────────────────────────────────────── -->
          <template v-if="mode === 'version'">
            <v-text-field
              data-testid="config-v2-versionCode-input"
              v-model="form.versionCode"
              label="Version code *"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-tag-outline"
              placeholder="V1"
              :readonly="isEditMode"
              :rules="[rules.required]"
              @update:model-value="form.versionCode = normalizeCode(form.versionCode)"
            />
            <v-text-field
              data-testid="config-v2-version-description-input"
              v-model="form.description"
              label="Description"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-text-box-outline"
              placeholder="รายละเอียดเวอร์ชันนี้"
            />
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  data-testid="config-v2-version-startDate-input"
                  v-model="form.effectiveStartDate"
                  label="Effective start date"
                  type="date"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-calendar-start"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  data-testid="config-v2-version-endDate-input"
                  v-model="form.effectiveEndDate"
                  label="Effective end date"
                  type="date"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-calendar-end"
                />
              </v-col>
            </v-row>
            <v-switch
              data-testid="config-v2-version-active-switch"
              v-model="form.isActive"
              label="Active (active ได้ 1 เวอร์ชันต่อปี — เวอร์ชันอื่นจะถูกปิด)"
              color="success"
              inset
              hide-details
            />
          </template>

          <!-- ── CLONE mode ───────────────────────────────────────────── -->
          <template v-else-if="mode === 'clone'">
            <v-alert
              v-if="sourceVersion"
              type="info"
              variant="tonal"
              density="comfortable"
              class="mb-4"
            >
              โคลนจากเวอร์ชัน <strong>{{ sourceVersion.versionCode }}</strong> — คัดลอกหมวด/กติกาไปยังเวอร์ชันใหม่
            </v-alert>
            <v-text-field
              data-testid="config-v2-clone-code-input"
              v-model="form.newVersionCode"
              label="New version code *"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-tag-plus-outline"
              placeholder="V2"
              :rules="[rules.required]"
              @update:model-value="form.newVersionCode = normalizeCode(form.newVersionCode)"
            />
            <v-text-field
              data-testid="config-v2-clone-description-input"
              v-model="form.description"
              label="Description"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-text-box-outline"
              placeholder="เช่น โคลนจาก V1"
            />
            <div class="d-flex flex-column">
              <v-switch
                data-testid="config-v2-clone-copyCategories-switch"
                v-model="form.copyCategories"
                label="คัดลอกหมวด (categories)"
                color="primary"
                inset
                hide-details
              />
              <v-switch
                data-testid="config-v2-clone-copyRules-switch"
                v-model="form.copyRules"
                label="คัดลอกกติกา (rules)"
                color="primary"
                inset
                hide-details
              />
              <v-switch
                data-testid="config-v2-clone-activate-switch"
                v-model="form.activate"
                label="ตั้งเวอร์ชันใหม่เป็น active"
                color="success"
                inset
                hide-details
              />
            </div>
          </template>

          <!-- ── RULE mode ────────────────────────────────────────────── -->
          <template v-else>
            <v-alert
              v-if="submitAttempted && ruleErrors.length"
              type="error"
              variant="tonal"
              density="compact"
              class="mb-4"
              data-testid="config-v2-rule-error"
            >
              <div class="font-weight-medium mb-1">กรุณาตรวจสอบข้อมูลให้ครบถ้วน</div>
              <ul class="cfg-v2-error-list">
                <li v-for="(msg, i) in ruleErrors" :key="i">{{ msg }}</li>
              </ul>
            </v-alert>

            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  data-testid="config-v2-rule-category-select"
                  v-model="form.categoryCode"
                  :items="categoryItems"
                  item-title="label"
                  item-value="code"
                  label="หมวด (Category)"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-shape-outline"
                  clearable
                  hint="เว้นว่าง = ยังไม่จัดหมวด (uncategorized)"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  data-testid="config-v2-rule-code-input"
                  v-model="form.fieldKey"
                  label="Field Key (fieldKey) *"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-identifier"
                  placeholder="เช่น LIFE_INSURANCE_SELF"
                  :readonly="isEditMode"
                  @update:model-value="form.fieldKey = normalizeCode(form.fieldKey)"
                />
              </v-col>
              <v-col cols="12" md="8">
                <v-text-field
                  data-testid="config-v2-rule-label-input"
                  v-model="form.label"
                  label="ชื่อที่แสดง (Label)"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-format-title"
                  placeholder="เช่น ประกันชีวิตตนเอง"
                />
              </v-col>
              <v-col cols="6" md="4">
                <v-text-field
                  data-testid="config-v2-rule-sortOrder-input"
                  v-model.number="form.sortOrder"
                  label="Sort order"
                  type="number"
                  min="0"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-sort-numeric-ascending"
                />
              </v-col>
            </v-row>

            <v-select
              data-testid="config-v2-rule-valueType-select"
              v-model="form.valueType"
              :items="valueTypeOptions"
              label="ชนิดค่า (ValueType) *"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-shape-outline"
            />
            <p class="text-caption text-medium-emphasis mt-n2 mb-2">
              ชนิดข้อมูลของคำตอบที่ FieldKey นี้เก็บ — text / number / boolean
            </p>

            <v-switch
              data-testid="config-v2-rule-active-switch"
              v-model="form.isActive"
              label="Active"
              color="success"
              inset
              hide-details
            />
          </template>
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-4 d-flex align-center flex-wrap ga-2">
        <div v-if="mode === 'rule'" class="cfg-v2-hint d-flex align-center">
          <v-icon size="16" class="mr-1">mdi-information-outline</v-icon>
          <span>การเปลี่ยนแปลงจะถูกพักไว้ — กด Save ที่หน้าหลักเพื่อบันทึกถาวร</span>
        </div>
        <v-spacer />
        <v-btn data-testid="config-v2-cancel-btn" variant="outlined" @click="close">
          <v-icon start>mdi-close</v-icon>
          ยกเลิก
        </v-btn>
        <v-btn
          data-testid="config-v2-submit-btn"
          color="primary"
          variant="elevated"
          @click="submit"
        >
          <v-icon start>{{ isEditMode ? 'mdi-check' : 'mdi-content-save' }}</v-icon>
          {{ submitText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// ValueType = the DATA TYPE of the answer this FieldKey stores. V2 has no calc, so the
// old calc-shaped types (RATE/CAP/AMOUNT/RANGE/EXPENSE_OPTIONS) are gone — lower-case
// tokens, same convention as the question item inputType/answerType.
const VALUE_TYPES = ['text', 'number', 'boolean'];
const VALUE_TYPE_OPTIONS = [
  { title: 'text — ข้อความ', value: 'text' },
  { title: 'number — ตัวเลข', value: 'number' },
  { title: 'boolean — ใช่ / ไม่ใช่', value: 'boolean' },
];
const DEFAULT_VALUE_TYPE = 'text';

/** Coerce anything unknown (incl. legacy RATE/CAP/… rows) to the default. */
function normalizeValueType(v) {
  const t = String(v || '').trim().toLowerCase();
  return VALUE_TYPES.includes(t) ? t : DEFAULT_VALUE_TYPE;
}


export default {
  name: 'TaxConfigV2Dialog',
  props: {
    modelValue: { type: Boolean, default: false },
    /** 'version' | 'clone' | 'rule' */
    mode: { type: String, default: 'version' },
    /** version DTO (version edit) or rule object (rule edit); null = create */
    initialData: { type: Object, default: null },
    /** source version DTO shown in clone mode */
    sourceVersion: { type: Object, default: null },
    /** categories of this version (rule mode category select) */
    categories: { type: Array, default: () => [] },
    /** sibling FieldKeys in this version (rule mode: duplicate-FieldKey guard) */
    existingFieldKeys: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue', 'submit'],
  data() {
    return {
      submitAttempted: false,
      valueTypeOptions: VALUE_TYPE_OPTIONS,
      form: this.buildForm(),
      rules: {
        required: (v) => (v !== null && v !== undefined && String(v).trim() !== '') || 'Required',
      },
    };
  },
  computed: {
    isEditMode() {
      if (this.mode === 'version') return Boolean(this.initialData && this.initialData.versionID != null);
      if (this.mode === 'rule') return Boolean(this.initialData && this.initialData.fieldKey);
      return false; // clone is always "create"
    },
    titleIcon() {
      if (this.mode === 'clone') return 'mdi-content-copy';
      if (this.mode === 'rule') return this.isEditMode ? 'mdi-pencil' : 'mdi-plus-circle';
      return this.isEditMode ? 'mdi-pencil' : 'mdi-source-branch-plus';
    },
    titleText() {
      if (this.mode === 'clone') return 'โคลนเวอร์ชัน (Clone version)';
      if (this.mode === 'rule') return this.isEditMode ? 'แก้ไขกติกา (Tax Rule V2)' : 'เพิ่มกติกา (Tax Rule V2)';
      return this.isEditMode ? 'แก้ไขเวอร์ชัน (Config Version V2)' : 'เพิ่มเวอร์ชัน (Config Version V2)';
    },
    submitText() {
      if (this.mode === 'clone') return 'Clone';
      if (this.mode === 'rule') return this.isEditMode ? 'บันทึกการแก้ไข' : 'เพิ่มในรายการ';
      return this.isEditMode ? 'Update' : 'Create';
    },
    categoryItems() {
      return (this.categories || [])
        .map((c) => ({
          code: c.categoryCode,
          label: `${c.categoryCode}${c.label ? ' - ' + c.label : ''}`,
        }))
        .filter((c) => c.code);
    },
    ruleErrors() {
      if (this.mode !== 'rule') return [];
      const e = [];
      const f = this.form;
      const code = String(f.fieldKey || '').trim();
      if (!code) e.push('ต้องกรอก Field Key');
      else if (!this.isEditMode && this.existingFieldKeys
        .map((c) => String(c || '').trim().toUpperCase())
        .includes(code.toUpperCase())) {
        e.push(`Field Key "${code}" มีอยู่แล้วในเวอร์ชันนี้`);
      }
      if (!VALUE_TYPES.includes(f.valueType)) e.push('ต้องเลือก ValueType (text / number / boolean)');
      return e;
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.resetForm();
    },
  },
  methods: {
    normalizeCode(value) {
      return String(value || '').replace(/\s+/g, '').toUpperCase();
    },
    buildForm() {
      return {
        // version
        versionCode: '',
        // clone
        newVersionCode: '',
        copyCategories: true,
        copyRules: true,
        activate: false,
        // shared version/clone
        description: '',
        effectiveStartDate: null,
        effectiveEndDate: null,
        isActive: false,
        // rule
        versionID: null,
        ruleID: null,
        categoryCode: null,
        fieldKey: '',
        label: '',
        sortOrder: 0,
        valueType: DEFAULT_VALUE_TYPE,
      };
    },
    resetForm() {
      const base = this.buildForm();
      this.submitAttempted = false;
      if (this.mode === 'version' && this.initialData) {
        const d = this.initialData;
        base.versionID = d.versionID ?? null;
        base.versionCode = d.versionCode || '';
        base.description = d.description || '';
        base.effectiveStartDate = (d.effectiveStartDate || '').slice(0, 10) || null;
        base.effectiveEndDate = (d.effectiveEndDate || '').slice(0, 10) || null;
        base.isActive = d.isActive ?? false;
      } else if (this.mode === 'clone') {
        base.copyCategories = true;
        base.copyRules = true;
        base.activate = false;
      } else if (this.mode === 'rule' && this.initialData) {
        const d = this.initialData;
        base.ruleID = d.ruleID ?? null;
        base.versionID = d.versionID ?? null;
        base.categoryCode = d.categoryCode || null;
        base.fieldKey = d.fieldKey || '';
        base.label = d.label || '';
        base.sortOrder = d.sortOrder ?? 0;
        base.valueType = normalizeValueType(d.valueType);
      }
      this.form = base;
      this.$nextTick(() => this.$refs.form?.resetValidation?.());
    },
    close() {
      this.$emit('update:modelValue', false);
    },
    async submit() {
      if (this.mode === 'version') return this.submitVersion();
      if (this.mode === 'clone') return this.submitClone();
      return this.submitRule();
    },
    async submitVersion() {
      const res = await this.$refs.form?.validate?.();
      if (res && res.valid === false) return;
      this.$emit('submit', {
        versionID: this.form.versionID,
        versionCode: this.normalizeCode(this.form.versionCode),
        description: this.form.description || '',
        effectiveStartDate: this.form.effectiveStartDate || null,
        effectiveEndDate: this.form.effectiveEndDate || null,
        isActive: Boolean(this.form.isActive),
      });
    },
    async submitClone() {
      const res = await this.$refs.form?.validate?.();
      if (res && res.valid === false) return;
      this.$emit('submit', {
        newVersionCode: this.normalizeCode(this.form.newVersionCode),
        description: this.form.description || '',
        copyCategories: Boolean(this.form.copyCategories),
        copyRules: Boolean(this.form.copyRules),
        activate: Boolean(this.form.activate),
      });
    },
    submitRule() {
      this.submitAttempted = true;
      if (this.ruleErrors.length) return;
      const f = this.form;
      this.$emit('submit', {
        ruleID: f.ruleID,
        versionID: f.versionID,
        categoryCode: f.categoryCode ? String(f.categoryCode).trim().toUpperCase() : null,
        fieldKey: this.normalizeCode(f.fieldKey),
        label: f.label || '',
        valueType: normalizeValueType(f.valueType),
        sortOrder: Number(f.sortOrder) || 0,
        isActive: f.isActive !== false,
      });
    },
  },
};
</script>

<style scoped>
.cfg-v2-error-list {
  margin: 0;
  padding-left: 18px;
  font-size: 0.85rem;
}

.cfg-v2-hint {
  font-size: 0.78rem;
  color: #6b7280;
  line-height: 1.3;
}
</style>
